"use client"

import { useMemo, useState, useEffect, useContext, useRef } from "react"
import { DataGrid } from "@mui/x-data-grid"
import {
  IconButton,
  Typography,
  Box,
  Paper,
  Button,
  Autocomplete,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  alpha,
  Checkbox,
  Collapse,
  useMediaQuery,
} from "@mui/material"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import CustomPagination from "./CustomPagination"

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility"
import ReplayIcon from "@mui/icons-material/Replay"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import SearchIcon from "@mui/icons-material/Search"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import PersonIcon from "@mui/icons-material/Person"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PointOfSaleIcon from "@mui/icons-material/PointOfSale"
import DeleteIcon from "@mui/icons-material/Delete"
import BarChartIcon from "@mui/icons-material/BarChart"
import TuneIcon from "@mui/icons-material/Tune"
import FilterListIcon from "@mui/icons-material/FilterList"
import ChannelIcon from "@mui/icons-material/Tv"

import { ThemeProvider, createTheme } from "@mui/material/styles"
import { ErpContext } from "../../context/ErpContext.js"
import "./TablaVentasComponent.css"
import { GridFooterContainer, GridFooter } from "@mui/x-data-grid"

import dayjs from "dayjs"

import SpinnerLading from "../misc/SpinnerComponent.js"
import ModalViewTablaVenta from "./ModalViewTablaVenta.js"
import ModalViewTablaDevolucion from "./ModalViewTablaDevolucion.js"
import GraficasVentasModal from "./graficas/graficas-ventas-modal"
import Swal from "sweetalert2"
import { reservePdfTarget } from "../../context/pdf_esqueletos/helpers/pdfDelivery.js"

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "rgb(244, 177, 14)",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2d3748",
      secondary: "#718096",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          fontWeight: 500,
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#4a4a4e",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: alpha("#5b5b5f", 0.08),
          },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#5b5b5f",
            color: "#fff",
            borderBottom: "none",
            borderRadius: "8px 8px 0 0",
            padding: "0 16px",
            height: "56px",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            color: "white !important",
            fontWeight: 600,
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-columnHeaderTitleContainer": {
            padding: "0 8px",
          },
          "& .MuiDataGrid-menuIconButton": {
            color: "rgba(255, 255, 255, 0.7) !important",
            "&:hover": {
              color: "white !important",
              backgroundColor: "rgba(255, 255, 255, 0.1)",
            },
          },
          "& .MuiDataGrid-iconButtonContainer": {
            color: "white !important",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid rgba(224, 224, 224, 0.5)",
            padding: "0 16px",
            fontSize: "0.875rem",
          },
          "& .MuiDataGrid-row": {
            "&:nth-of-type(even)": {
              backgroundColor: "rgba(0, 0, 0, 0.02)",
            },
            "&:hover": {
              backgroundColor: "rgba(66, 153, 225, 0.08)",
              transition: "background 0.2s ease",
            },
            transition: "background 0.2s ease",
            cursor: "pointer",
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: "#fff",
            borderTop: "1px solid rgba(224, 224, 224, 0.5)",
            display: "none !important",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            background: "#f1f1f1",
            borderRadius: "10px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            backgroundColor: "#c1c1c1",
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: "#a1a1a1",
            },
          },
        },
      },
    },
  },
});

export default function TablaVentas() {
  const {
    getMoneda,
    getTipoTransaccion,
    getUbicacion,
    ubicaciones,
    getNegocio,
    getMedioDePago,
    getSubCategoriaTransaccion,
    getTransaccionItemID,
    generarFacturaPDF,
    getUsuario,
    getTransaccionTransformedVentas,
    getTransaccionTransformedVentasAllData, // NUEVO: para gráficas, esto utiliza el filtro de ventas pero no tiene en cuenta el paginado
    getEntidadByID,
    getItemById,
    getItemUbicacionFilter,
    generarRemitoPDF,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    getTransaccionMontoTotalByTipoTransaccion,
    updateDeleteTransaccion,
    isNavbarCollapsed,
    getFechaHoraActualClient,
    getParametrosGlobales,
    getCanalEntidad,
    getTipoMedioDePago,
    tipoMedioDePago,
    getMedioDePagoIdTipoEspecifico,
    medioDePagoIdTipoEspecifico,
  } = useContext(ErpContext)

  // ===== Responsive flags =====
  // "Laptop para abajo": aprox <= 1200px
  const isLaptopDown = useMediaQuery("(max-width:1200px)")
  const isMobile = useMediaQuery("(max-width:600px)")

  // ===== Estados principales =====
  const [openModal, setOpenModal] = useState(false)
  const [transaccionItems, setTransaccionItems] = useState([])
  const [selectedTransaccionId, setSelectedTransaccionId] = useState(null)
  const [selectedTransaccion, setSelectedTransaccion] = useState(null)
  const [selectedTransaccionEntidad, setSelectedTransaccionEntidad] = useState(null)
  const [rows, setRows] = useState([])
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const today = dayjs()
  const [startDate, setStartDate] = useState(today.subtract(30, "day"))
  const [endDate, setEndDate] = useState(today)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)
  const [minVentas, setMinVentas] = useState(0)
  const [maxVentas, setMaxVentas] = useState(0)
  const [itemsMap, setItemsMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [sortedData, setSortedData] = useState([])
  const [dniCliente, setDniCliente] = useState("")
  const [searchTextCliente, setSearchTextCliente] = useState("")
  const [filtersActive, setFiltersActive] = useState(false)
  const [mostrarDeletes, setMostrarDeletes] = useState(false)
  const [idTransaccionSearch, setIdTransaccionSearch] = useState(null)
  const [openModalDevolucion, setOpenModalDevolucion] = useState(false)
  const [canalesEntidades, setCanalesEntidades] = useState([])
  const [selectedCanalEntidad, setSelectedCanalEntidad] = useState(null)
  const [selectedTipoMedioDePago, setSelectedTipoMedioDePago] = useState(null)
  const [selectedMedioDePago, setSelectedMedioDePago] = useState(null)

  // Gráficas
  const [openGraficasModal, setOpenGraficasModal] = useState(false)
  const [loadingGraficas, setLoadingGraficas] = useState(false) // estado de carga para graficas
  const [masFiltrosActive, setMasFiltrosActive] = useState(false)
  const [tipoTransaccionFiltro, setTipoTransaccionFiltro] = useState("todos")
  const [datosOriginales, setDatosOriginales] = useState([])
  const [datosGraficas, setDatosGraficas] = useState([]) //datos para graficas

  // NUEVO: toggle para ocultar/mostrar TODO el bloque de filtros en laptop/mobile
  const [filtersOpen, setFiltersOpen] = useState(!isLaptopDown)
  useEffect(() => {
    // Si pasa a laptop/mobile, colapso filtros; en desktop, los muestro siempre
    setFiltersOpen(!isLaptopDown)
  }, [isLaptopDown])

  // ===== Fechas =====
  const increaseDate = () => setStartDate((prev) => (prev ? prev.add(1, "day") : dayjs()))
  const decreaseDate = () => setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()))

  // ===== Efectos iniciales =====
  useEffect(() => {
    const today = dayjs()
    setStartDate(today.subtract(30, "day"))
    setEndDate(today)
    fetchData(
      startDate,
      page,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch,
      tipoTransaccionFiltro,
      selectedCanalEntidad
    )
    fetchDataInicio()
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchData(
      startDate,
      page,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch,
      tipoTransaccionFiltro,
      selectedCanalEntidad,
    )
    setLoading(false)
  }, [startDate])

  useEffect(() => {
    fetchData(
      startDate,
      page,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch,
      tipoTransaccionFiltro,
      selectedCanalEntidad,
    )
    setLoading(false)
  }, [page])

  // Efecto para cargar medios de pago cuando se selecciona un tipo
  useEffect(() => {
    if (selectedTipoMedioDePago) {
      getMedioDePagoIdTipoEspecifico(selectedTipoMedioDePago)
    }
  }, [selectedTipoMedioDePago])

  // ===== Filtros / datos =====
  const buttonFiltrado = () => {
    fetchData(
      startDate,
      1,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch,
      tipoTransaccionFiltro,
      selectedCanalEntidad,
    )
  }

  const fetchDataInicio = async () => {
    try {

      await getUbicacion()
      await getTipoMedioDePago()
      const canalEntidad = await getParametrosGlobales(["canalEntidad"])
      console.log("canalEntidad canalEntidad :", canalEntidad.canalEntidad)
      if (canalEntidad.canalEntidad == 1) {
        const datosCanalesEntidades = await getCanalEntidad()
        console.log("datosCanalesEntidades:", datosCanalesEntidades)
        setCanalesEntidades(datosCanalesEntidades)
      }
      
    } catch (error) {
      console.error("Error al cargar ubicaciones:", error)
    }
  }

  const handleMinChange = (e) => {
    const newMin = Number(e.target.value)
    if (newMin <= maxVentas) setMinVentas(newMin)
  }

  const handleMaxChange = (e) => {
    const newMax = Number(e.target.value)
    if (newMax >= minVentas) setMaxVentas(newMax)
  }

  const fetchData = async (
    date,
    page,
    endDate,
    dniCliente,
    selectedUbicacion,
    valorMin,
    valorMax,
    mostrarDeletes,
    idTransaccionSearch,
    tipoTransaccionFiltro,
    canalEntidad,
    limit = 100,
  ) => {
    const controller = new AbortController()
    const signal = controller.signal
    try {

      setLoading(true)

      const validDate = date ? date : dayjs()
      let formattedDate = validDate.format("YYYY-MM-DD")
      let formattedDate2 = endDate
      let idDni = null
      let idSelectedUbicacion = null

      if (endDate && typeof endDate.format === "function") {
        formattedDate2 = endDate.format("YYYY-MM-DD")
      }
      if (dniCliente != null) idDni = dniCliente.id
      if (selectedUbicacion != null) idSelectedUbicacion = selectedUbicacion.id

      const result1 = await getFechaHoraActualClient(formattedDate)
      formattedDate = dayjs(result1.fechaServidor).startOf("day").toDate()

      const result2 = await getFechaHoraActualClient(formattedDate2)
      formattedDate2 = dayjs(result2.fechaServidor).endOf("day").toDate()

      console.log("canalEntidad ", canalEntidad)

      const tipoMedioDePagoId = selectedTipoMedioDePago ? selectedTipoMedioDePago.id : null
      const medioDePagoId = selectedMedioDePago ? selectedMedioDePago.id : null

      const data = await getTransaccionTransformedVentas(
        1,
        formattedDate,
        page,
        limit,
        { signal },
        formattedDate2,
        idDni,
        idSelectedUbicacion,
        valorMin,
        valorMax,
        mostrarDeletes,
        idTransaccionSearch,
        "traerDevolucion",
        "", // Buscar por CAE
        tipoTransaccionFiltro,
        canalEntidad?.id || null,
        false, // soloFacturadas
        tipoMedioDePagoId,
        medioDePagoId,
      )

      setDatosOriginales(data.data)

      const transaccionTransformedReforged = data.data.map((transaccion) => {
        const pagos = transaccion.pagos.map((pago) => ({
          descripcion: pago.medioDePago,
          monto: pago.montoTotal,
        }))

        const formattedDate = new Intl.DateTimeFormat("es-AR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(new Date(transaccion.fechaHoraCreacion))

        return {
          id: transaccion.id,
          createdAt: formattedDate,
          montoTotal: transaccion.montoTotal,
          idMoneda: transaccion.pagos?.[0]?.cotizacion || "",
          idTipoTransaccion: transaccion.tipoTransaccion?.descripcion || "",
          pagos,
          descripcionEntidad: transaccion.entidad?.descripcion || "",
          dniCuitCuil: transaccion.entidad?.dniCuitCuil || "",
          apellido: transaccion.entidad?.apellido || "",
          direccion: transaccion.entidad?.direccion || "",
          localidad: transaccion.entidad?.localidad || "",
          provincia: transaccion.entidad?.provincia || "",
          usuario: transaccion.usuario?.usuario || "",
          negocio: transaccion.negocio?.descripcion || "",
          ubicacion: transaccion.ubicacion?.descripcion || "",
          idEntidadNumero: transaccion.idEntidad,
          fechaHoraCreacion: transaccion.fechaHoraCreacion,
          impuestos: transaccion.impuestos,
          montoDescuento: transaccion.montoDescuento,
          tipoFacturaDescripcion: transaccion.tipoFacturaDescripcion,
          porcentajeDescuento: transaccion.porcentajeDescuento,
          tipoTransaccion: transaccion.tipoTransaccion,
          idTipoTransaccionID: transaccion.idTipoTransaccion,
          transaccionAsociada: transaccion.transaccionAsociada || null,
          canal: transaccion.entidad?.canalEntidad?.descripcion || "",
        }
      })

      console.log("transaccionTransformedReforged", transaccionTransformedReforged)

      setPage(data.currentPage)
      setTotalPages(data.totalPages)
      setTotalCount(data.total)
      setMontoTotalTransacciones(data.totalMontoVentas)
      setSortedData(transaccionTransformedReforged)
    } catch (error) {
      console.error("Error al cargar los datos:", error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) =>
    new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS" }).format(value)

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1)
      setLoading(false)
    }
  }, [searchTextCliente])

  useEffect(() => {
    const fetchItemsDetails = async () => {
      const itemsDetails = {}
      await Promise.all(
        transaccionItems.map(async (transaccionItem) => {
          const foundItem = await getItemById(transaccionItem.idItem)
          if (foundItem) itemsDetails[transaccionItem.idItem] = foundItem
        }),
      )
      setItemsMap(itemsDetails)
    }
    fetchItemsDetails()
    setLoading(false)
  }, [transaccionItems, getItemById])

  // Columnas generadas dinámicamente usando useMemo
  const columns = useMemo(() => {
    const baseColumns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.3,
      minWidth: 70,
      headerAlign: "center",
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Fecha",
      flex: 1,
      headerAlign: "center",
      minWidth: 200,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
          <CalendarTodayIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      type: "number",
      flex: 0.8,
      minWidth: 130,
      headerAlign: "center",
      sortable: true,
      renderCell: (params) => {
        const esDevolucion = params.row?.idTipoTransaccionID === 6
        return (
          <Chip
            icon={<AttachMoneyIcon fontSize="small" />}
            label={params.value?.toLocaleString("es-AR") || "0.00"}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: esDevolucion ? alpha("#9e9e9e", 0.1) : alpha("#4CAF50", 0.1),
              color: esDevolucion ? "#757575" : "#4CAF50",
              "& .MuiChip-icon": { color: esDevolucion ? "#757575" : "#4CAF50" },
              display: "flex",
            }}
          />
        )
      },
    },
    {
      field: "descripcionEntidad",
      headerName: "Cliente",
      flex: 1.2,
      headerAlign: "center",
      minWidth: 150,
      sortable: true,
      valueGetter: (value, row) => {
        if (!row) return "";
        return `${row.descripcionEntidad || ""} ${row.apellido || ""}`.trim();
      },
      renderCell: (params) => (
        <Tooltip title={`${params.row.descripcionEntidad} ${params.row.apellido || ""}`}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2" noWrap fontWeight={"500"}>
              {params.row.descripcionEntidad} {params.row.apellido || ""}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "idTipoTransaccion",
      headerName: "Tipo",
      flex: 0.8,
      minWidth: 120,
      headerAlign: "center",
      sortable: true,
      renderCell: (params) => {
        const esDevolucion = params.row?.idTipoTransaccionID === 6
        return (
          <Chip
            label={params.value}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: esDevolucion ? alpha("#9e9e9e", 0.1) : alpha("#4CAF50", 0.1),
              color: esDevolucion ? "#757575" : "#4CAF50",
              display: "flex",
            }}
          />
        )
      },
    },
    {
      field: "pagos",
      headerName: "Medio de Pago",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      sortable: true,
      valueGetter: (value, row) => {
        if (!row) return "N/A";
        const pagos = Array.isArray(row.pagos) ? row.pagos : [];
        if (pagos.length > 1) return "Múltiple";
        return pagos[0]?.descripcion || "N/A";
      },
      renderCell: (params) => {
        const pagos = Array.isArray(params.row.pagos) ? params.row.pagos : []
        if (pagos.length > 1) {
          return <Chip label="Múltiple" size="small" color="info" sx={{ fontWeight: "bold", display: "flex" }} />
        }
        return (
          <Chip
            icon={<PointOfSaleIcon fontSize="small" />}
            label={pagos[0]?.descripcion || "N/A"}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              color: "#00AAE4",
              borderColor: alpha("#00AAE4", 0.5),
              "& .MuiChip-icon": { color: "#00AAE4" },
              display: "flex",
            }}
          />
        )
      },
    },
    {
      field: "usuario",
      headerName: "Vendedor",
      flex: 0.8,
      minWidth: 150,
      headerAlign: "center",
      sortable: true,
      renderCell: (params) => <Typography variant="body2" sx={{ fontStyle: "italic" }}>{params.value}</Typography>,
    },
    {
      field: "ubicacion",
      headerName: "Ubicación",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      headerName: "Venta asociada",
      field: "transaccionAsociada",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => {
        if (params.value !== null && params.value !== undefined) {
          return (
            <Button variant="contained" size="small" onClick={() => handleVerTransaccionAsociada(params.value)}>
              {params.value}
            </Button>
          )
        } else {
          return <Typography variant="body2">No existe</Typography>
        }
      },
    },
    {
      field: "action",
      headerName: "Acciones",
      flex: 0.8,
      minWidth: 150,
      headerAlign: "center",
      sortable: false,
      renderCell: (params) => {
        const esDevolucion = params.row?.idTipoTransaccionID === 6
        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Ver detalles">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => handleOpenModal(params.row)}
                size="small"
                sx={{ bgcolor: alpha("#5b5b5f", 0.1), "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) } }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {!esDevolucion && (
              <>
                <Tooltip title="Descargar PDF">
                  <IconButton
                    className="colorGeneralParaIconos"
                    onClick={() => handleDownloadPDF(params.row)}
                    size="small"
                    sx={{ bgcolor: alpha("rgb(244, 177, 14)", 0.1), "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) } }}
                  >
                    <PictureAsPdfIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Descargar remito">
                  <IconButton
                    className="colorGeneralParaIconos"
                    onClick={() => handleDownloadRemito(params.row)}
                    size="small"
                    sx={{ bgcolor: alpha("#4CAF50", 0.1), "&:hover": { bgcolor: alpha("#4CAF50", 0.2) } }}
                  >
                    <ReceiptLongIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Tooltip title="Borrar Transaccion">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => borrarTransaccion(params.row)}
                size="small"
                sx={{ bgcolor: alpha("rgb(244, 177, 14)", 0.1), "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )
      },
    },
  ];

  // Agregar columna de canal si está habilitado
  if (canalesEntidades && canalesEntidades.length > 0) {
    baseColumns.splice(-1, 0, {
      field: "canal",
      headerName: "Canal",
      headerAlign: "center",
      flex: 1,
      minWidth: 120,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ChannelIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value || "Sin canal"}</Typography>
        </Box>
      ),
    });
  }

  return baseColumns;
}, [canalesEntidades])

  const totalVentas = useMemo(() => rows.reduce((acc, row) => acc + (row.montoTotal || 0), 0), [rows])

  const CustomFooter = () => (
    <GridFooterContainer>
      <div
        style={{
          padding: "10px",
          fontWeight: "bold",
          fontSize: "16px",
          textAlign: "right",
          width: "100%",
          backgroundColor: "#f5f5f5",
        }}
      >
        Total Ventas: <span style={{ color: "#4CAF50" }}>${totalVentas.toLocaleString("es-AR")}</span>
      </div>
      <GridFooter />
    </GridFooterContainer>
  )

  const handleVerTransaccionAsociada = async (id) => {
    await fetchData(
      startDate,
      page,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      id,
      tipoTransaccionFiltro,
      selectedCanalEntidad,
    )
  }

  const borrarTransaccion = async (transaccionRow) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Vas a eliminar la transacción con ID ${transaccionRow.id}. Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    })

    if (result.isConfirmed) {
      try {
        await updateDeleteTransaccion(transaccionRow.id)
        await fetchData(
          startDate,
          page,
          endDate,
          dniCliente,
          selectedUbicacion,
          minVentas,
          maxVentas,
          mostrarDeletes,
          idTransaccionSearch,
          tipoTransaccionFiltro,
          selectedCanalEntidad,
        )
        Swal.fire({ title: "Eliminado", text: `La transacción ${transaccionRow.id} ha sido eliminada.`, icon: "success", timer: 1500, showConfirmButton: false })
      } catch (error) {
        console.error("Error al borrar transacción:", error)
        Swal.fire({ title: "Error", text: "No se pudo eliminar la transacción. Intenta de nuevo.", icon: "error" })
      }
    }
  }

  const handleDownloadPDF = async (transaccionRow) => {
    const deliveryContext = { targetWindow: reservePdfTarget() }
    try {
      setLoading(true)
      const transaccionEncontrada = await getTransaccionItemID(transaccionRow.id)
      console.log("transaccionEncontrada: ", transaccionEncontrada)
      const entidadCliente = await getEntidadByID(transaccionRow.idEntidadNumero)
      const itemsTransaccion = await Promise.all(
        transaccionEncontrada.map(async (transItem) => {
          const foundItem = await getItemById(transItem.idItem)
          const precio = transItem.precio || 0
          return {
            idItem: transItem.idItem,
            codigo: foundItem ? foundItem.codigo : "N/A",
            descripcion: foundItem ? foundItem.descripcion : "Descripción no disponible",
            cantidad: transItem.cantidad || 0,
            precio: precio,
            porcentajeDescuento: transItem.porcentajeDescuento || 0,
            total: (precio * transItem.cantidad).toFixed(2),
          }
        }),
      )
      generarFacturaPDF(transaccionRow, itemsTransaccion, entidadCliente, "tablaVenta", transaccionRow.impuestos, false, deliveryContext)
    } catch (error) {
      console.error("Error al generar PDF:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleMostrarDeletesChange = (event) => setMostrarDeletes(event.target.checked)

  const handleDownloadRemito = async (transaccionRow) => {
    const remitoDelivery = { targetWindow: reservePdfTarget() }
    try {
      setLoading(true)
      const transaccionEncontrada = await getTransaccionItemID(transaccionRow.id)
      const entidadCliente = await getEntidadByID(transaccionRow.idEntidadNumero)
      const itemsTransaccion = await Promise.all(
        transaccionEncontrada.map(async (transItem) => {
          const foundItem = await getItemById(transItem.idItem)
          const foundItemUbicacion = foundItem ? await getItemUbicacionFilter(foundItem.id) : null
          const precio = foundItemUbicacion[0]?.precio || 0
          return {
            codigo: foundItem ? foundItem.codigo : "N/A",
            descripcion: foundItem ? foundItem.descripcion : "Descripción no disponible",
            cantidad: transItem.cantidad || 0,
            precio: transItem.precio,
            porcentajeDescuento: transItem.porcentajeDescuento || 0,
            total: (precio * transItem.cantidad).toFixed(2),
          }
        }),
      )
      const tipoFacturaDescripcion = transaccionRow.tipoFacturaDescripcion != "" ? false : true
      generarRemitoPDF(
        transaccionRow,
        itemsTransaccion,
        entidadCliente,
        transaccionRow.usuario,
        tipoFacturaDescripcion,
        transaccionRow.impuestos,
        remitoDelivery,
      )
    } catch (error) {
      console.error("Error al generar remito:", error)
    } finally {
      setLoading(false)
    }
  }

  const [calculatedTotal, setCalculatedTotal] = useState(null)
  const [totalMontoDescuento, setTotalMontoDescuento] = useState(null)
  const [totalPorcentajeDescuento, setPorcentajeDescuento] = useState(null)
  const [montoSinImpuesto, setMontoSinImpuesto] = useState(null)

  const handleOpenModal = async (transaccionRow) => {
    try {
      setLoading(true)
      setSelectedTransaccion(transaccionRow)
      const entidadCliente = await getEntidadByID(transaccionRow.idEntidadNumero)
      setSelectedTransaccionEntidad(entidadCliente)

      const items = await getTransaccionItemID(transaccionRow.id)
      setTransaccionItems(items)

      const { montoTotal, impuestos = [], montoDescuento = 0, porcentajeDescuento = 0 } = transaccionRow
      const totalSinImpuestos = impuestos.reduce((total, impuesto) => total - (impuesto.montoTotal || 0), montoTotal)
      const totalSinImpuestosYDescuento = totalSinImpuestos + montoDescuento
      const totalFinal = totalSinImpuestosYDescuento * (1 - porcentajeDescuento / 100)

      setCalculatedTotal(totalFinal)
      setTotalMontoDescuento(montoDescuento)
      setPorcentajeDescuento(porcentajeDescuento)
      setMontoSinImpuesto(totalSinImpuestos)

      switch (transaccionRow.idTipoTransaccionID) {
        case 1: // venta
          setOpenModal(true)
          break
        case 6: // devolucion
          setOpenModalDevolucion(true)
          break
      }
    } catch (error) {
      console.error("Error al abrir modal:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setOpenModalDevolucion(false)
  }

  const handleOpenGraficasModal = async () => {
    setLoadingGraficas(true) // estado de carga para graficas
    setOpenGraficasModal(true) // abrir modal de graficas
    
    try {
      const validDate = startDate ? startDate : dayjs() // fecha de inicio
      let formattedDate = validDate.format("YYYY-MM-DD") // fecha de inicio formateada
      let formattedDate2 = endDate // fecha de fin
      let idDni = null // id del cliente
      let idSelectedUbicacion = null // id de la ubicación

      if (endDate && typeof endDate.format === "function") {
        formattedDate2 = endDate.format("YYYY-MM-DD") // fecha de fin formateada
      }
      if (dniCliente != null) idDni = dniCliente.id // id del cliente
      if (selectedUbicacion != null) idSelectedUbicacion = selectedUbicacion.id // id de la ubicación

      const result1 = await getFechaHoraActualClient(formattedDate) // pedimos la fecha del servidor
      formattedDate = dayjs(result1.fechaServidor).startOf("day").toDate() // formateamos la fecha

      const result2 = await getFechaHoraActualClient(formattedDate2) // pedimos la fecha del servidor
      formattedDate2 = dayjs(result2.fechaServidor).endOf("day").toDate() // formateamos la fecha

      // Llamar a la nueva función SIN PAGINADO para obtener todos los datos
      const allData = await getTransaccionTransformedVentasAllData(
        1, // tipoTransaccion: 1 = venta
        formattedDate, // fecha de inicio
        formattedDate2, // fecha de fin
        idDni, // id del cliente
        idSelectedUbicacion, // id de la ubicación
        minVentas, // monto mínimo
        maxVentas, // monto máximo
        mostrarDeletes, // mostrar eliminados
        idTransaccionSearch, // id de la transacción
        "traerDevolucion", // traer devoluciones
        "", // Buscar por CAE
        tipoTransaccionFiltro, // tipo de transacción
        selectedCanalEntidad?.id || null // canal de entidad
      )

      console.log("Datos completos para gráficas:", allData)
      setDatosGraficas(allData.data)
    } catch (error) {
      console.error("Error al cargar datos para gráficas:", error)
      setDatosGraficas([])
    } finally {
      setLoadingGraficas(false)
    }
  }
  const handleCloseGraficasModal = () => setOpenGraficasModal(false)
  const mostrarFiltros = () => setMasFiltrosActive(!masFiltrosActive)

  const resetFilters = () => {
    setDniCliente(null)
    setSelectedUbicacion(null)
    setSearchTextCliente("")
    setMinVentas(0)
    setMaxVentas(0)
    setMostrarDeletes(false)
    setIdTransaccionSearch("")

    const today = dayjs()
    setStartDate(today.subtract(30, "day"))
    setEndDate(today)

    setTipoTransaccionFiltro("todos")
    setSelectedCanalEntidad(null)
    setSelectedTipoMedioDePago(null)
    setSelectedMedioDePago(null)
    setMasFiltrosActive(false)
    fetchData(startDate, 1, endDate, null, null, 0, 0, false, null, null, null)
  }

  if (loading) return <SpinnerLading />

  return (
    <div
      className="tablaVentasContainer"
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        width: "100%",
        // Desktop: sin scroll; Laptop/Mobile: permitir scroll de la página
        overflow: isLaptopDown ? "auto" : "hidden",
        position: "relative",
      }}
    >
      <ThemeProvider theme={theme}>
        {/* Barra compacta para Laptop/Mobile: botón de Filtros + acciones clave */}
        {isLaptopDown && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              alignItems: "center",
              justifyContent: "space-between",
              mb: 1,
              px: 1,
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen((v) => !v)}
              sx={{ textTransform: "none" }}
            >
              {filtersOpen ? "Ocultar filtros" : "Mostrar filtros"}
            </Button>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={buttonFiltrado}
                startIcon={<SearchIcon />}
                sx={{
                  backgroundColor: "#5b5b5f",
                  textTransform: "none",
                  color: "white",
                  "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                }}
              >
                Buscar
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={handleOpenGraficasModal}
                startIcon={<BarChartIcon />}
                sx={{
                  backgroundColor: theme.palette.secondary.main,
                  textTransform: "none",
                  color: "white",
                  "&:hover": { backgroundColor: alpha(theme.palette.secondary.main, 0.8) },
                }}
              >
                Gráficas
              </Button>
            </Box>
          </Box>
        )}

        {/* Contenedor de filtros (siempre visible en Desktop; colapsable en Laptop/Mobile) */}
        <Collapse in={filtersOpen || !isLaptopDown} timeout={250} unmountOnExit>
          <Paper
            className="diseñoFiltroTransaccionesVenta"
            elevation={3}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.9),
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {/* Filtro de fechas */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, flex: 1, minWidth: 250 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <IconButton onClick={decreaseDate} color="primary" size="small">
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <DatePicker
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    format="DD/MM/YYYY"
                    label="Desde"
                    slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 } } }}
                    sx={{ width: 150 }}
                  />
                  <DatePicker
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    format="DD/MM/YYYY"
                    label="Hasta"
                    slotProps={{ textField: { size: "small", inputProps: { maxLength: 10 } } }}
                    sx={{ width: 150 }}
                  />
                  <IconButton onClick={increaseDate} color="primary" size="small">
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </LocalizationProvider>
              </Box>

              {/* Filtro de montos */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, flex: 1, minWidth: 200 }}>
                <TextField
                  label="Mínimo"
                  type="number"
                  size="small"
                  value={minVentas}
                  onChange={handleMinChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={{ width: 150 }}
                />
                <TextField
                  label="Máximo"
                  type="number"
                  size="small"
                  value={maxVentas}
                  onChange={handleMaxChange}
                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                  sx={{ width: 150 }}
                />
              </Box>

              {/* Filtro de cliente y ubicación */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 2, minWidth: 300 }}>
                <TextField
                  label="Buscar por ID"
                  type="number"
                  size="small"
                  value={idTransaccionSearch}
                  onChange={(e) => setIdTransaccionSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: "20%" }}
                />
                <Autocomplete
                  size="small"
                  disablePortal
                  options={entidadesClientesTresLetras.map((it) => ({
                    label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
                    ...it,
                  }))}
                  value={dniCliente}
                  onChange={(event, newValue) => setDniCliente(newValue || null)}
                  onInputChange={(event, newInputValue) => setSearchTextCliente(newInputValue)}
                  sx={{ flex: 1 }}
                  renderInput={(params) => <TextField {...params} label="Cliente" variant="outlined" />}
                />
                <Autocomplete
                  size="small"
                  disablePortal
                  options={ubicaciones.map((it) => ({ id: it.id, label: it.descripcion }))}
                  value={selectedUbicacion}
                  onChange={(event, newValue) => setSelectedUbicacion(newValue)}
                  sx={{ flex: 1 }}
                  renderInput={(params) => <TextField {...params} label="Ubicación" variant="outlined" />}
                />
                {/* CheckBox para mostrar eliminados */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2">Mostrar eliminados</Typography>
                  <Checkbox checked={mostrarDeletes} onChange={(e) => setMostrarDeletes(e.target.checked)} color="primary" />
                </Box>
              </Box>

              {/* Botones de acción (siguen estando aquí para desktop) */}
              <Box sx={{ display: { xs: "none", lg: "flex" }, alignItems: "center", gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={buttonFiltrado}
                  startIcon={<SearchIcon />}
                  sx={{
                    backgroundColor: "#5b5b5f",
                    textTransform: "none",
                    padding: "8px 16px",
                    color: "white",
                    "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                  }}
                >
                  Buscar
                </Button>

                <Button
                  variant="contained"
                  size="small"
                  onClick={handleOpenGraficasModal}
                  startIcon={<BarChartIcon />}
                  sx={{
                    backgroundColor: theme.palette.secondary.main,
                    textTransform: "none",
                    padding: "8px 16px",
                    color: "white",
                    "&:hover": { backgroundColor: alpha(theme.palette.secondary.main, 0.8) },
                  }}
                >
                  Mostrar Gráficas
                </Button>

                <Tooltip title={filtersActive ? "Resetear filtros" : "Refrescar"}>
                  <IconButton
                    onClick={resetFilters}
                    color="primary"
                    size="small"
                    sx={{
                      bgcolor: filtersActive ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                      "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                    }}
                  >
                    {filtersActive ? <FilterAltOffIcon /> : <ReplayIcon />}
                  </IconButton>
                </Tooltip>

                <Tooltip title={masFiltrosActive ? "Ocultar Filtros" : "Mostrar Más Filtros"}>
                  <IconButton
                    onClick={mostrarFiltros}
                    color="primary"
                    size="small"
                    sx={{
                      bgcolor: filtersActive ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                      "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                    }}
                  >
                    <TuneIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Filtros adicionales */}
            {masFiltrosActive && (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                  mt: 2,
                  p: 2,
                  backgroundColor: alpha(theme.palette.background.paper, 0.5),
                  borderRadius: 1,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  alignItems: "flex-start",
                }}
              >
                {/* Filtro por tipo de transacción */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    minWidth: { xs: "100%", md: 300 },
                  }}
                >
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Tipo de transacción:
                  </Typography>
                  <Autocomplete
                    size="small"
                    disablePortal
                    options={[
                      { id: "todos", label: "Todos" },
                      { id: "ventas", label: "Solo Ventas" },
                      { id: "devoluciones", label: "Solo Devoluciones" },
                    ]}
                    value={{
                      id: tipoTransaccionFiltro,
                      label:
                        tipoTransaccionFiltro === "todos"
                          ? "Todos"
                          : tipoTransaccionFiltro === "ventas"
                          ? "Solo Ventas"
                          : "Solo Devoluciones",
                    }}
                    onChange={(event, newValue) => setTipoTransaccionFiltro(newValue?.id || "todos")}
                    sx={{ flex: 1, minWidth: 150 }}
                    renderInput={(params) => <TextField {...params} label="Filtrar por" variant="outlined" />}
                  />
                </Box>

                {/* Filtro por canal de entidad */}
                {canalesEntidades && canalesEntidades.length > 0 && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flex: 1,
                      minWidth: { xs: "100%", md: 300 },
                    }}
                  >
                    <Typography variant="body2" sx={{ minWidth: 120 }}>
                      Canal de entidad:
                    </Typography>
                    <Autocomplete
                      size="small"
                      disablePortal
                      options={[
                        { id: null, label: "Todos los canales" },
                        ...canalesEntidades.map((canal) => ({
                          id: canal.id,
                          label: canal.descripcion || `Canal ${canal.id}`,
                        })),
                      ]}
                      value={
                        selectedCanalEntidad
                          ? {
                              id: selectedCanalEntidad.id,
                              label: selectedCanalEntidad.label,
                            }
                          : { id: null, label: "Todos los canales" }
                      }
                      onChange={(event, newValue) => setSelectedCanalEntidad(newValue?.id ? newValue : null)}
                      sx={{ flex: 1, minWidth: 150 }}
                      renderInput={(params) => <TextField {...params} label="Seleccionar canal" variant="outlined" />}
                    />
                  </Box>
                )}

                {/* Filtro por tipo de medio de pago */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    minWidth: { xs: "100%", md: 300 },
                  }}
                >
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Tipo medio pago:
                  </Typography>
                  <Autocomplete
                    size="small"
                    disablePortal
                    options={tipoMedioDePago}
                    getOptionLabel={(option) => option.descripcion || ""}
                    value={selectedTipoMedioDePago}
                    onChange={(event, newValue) => {
                      setSelectedTipoMedioDePago(newValue)
                      if (!newValue) {
                        setSelectedMedioDePago(null)
                      }
                    }}
                    sx={{ flex: 1, minWidth: 150 }}
                    renderInput={(params) => <TextField {...params} label="Seleccionar tipo" variant="outlined" />}
                  />
                </Box>

                {/* Filtro por medio de pago específico */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                    minWidth: { xs: "100%", md: 300 },
                  }}
                >
                  <Typography variant="body2" sx={{ minWidth: 120 }}>
                    Medio de pago:
                  </Typography>
                  <Autocomplete
                    size="small"
                    disablePortal
                    options={medioDePagoIdTipoEspecifico}
                    getOptionLabel={(option) => option.descripcion || ""}
                    value={selectedMedioDePago}
                    onChange={(event, newValue) => {
                      setSelectedMedioDePago(newValue)
                    }}
                    disabled={!selectedTipoMedioDePago}
                    sx={{ flex: 1, minWidth: 150 }}
                    renderInput={(params) => <TextField {...params} label="Seleccionar medio" variant="outlined" />}
                  />
                </Box>
              </Box>
            )}
          </Paper>
        </Collapse>

        {/* Contenedor de la tabla (scrollable siempre dentro de la tarjeta) */}
        <Paper
          sx={{
            flex: 1,
            m: 1,
            borderRadius: 3,
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
            position: "relative",
            // Desktop fijo; laptop/mobile dinámico según si filtros están abiertos
            height: {
              xs: filtersOpen ? "58vh" : "70vh",
              sm: filtersOpen ? "60vh" : "72vh",
              md: filtersOpen ? "62vh" : "74vh",
              lg: "500px",
            },
          }}
        >
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            className="tablaVentasDataGrid"
            rows={sortedData}
            columns={columns}
            getRowId={(row) => row.id}
            rowCount={totalPages * 100}
            pagination
            paginationMode="server"
            getRowHeight={() => 35}
            headerHeight={35}
            getRowClassName={(params) => (params.row?.idTipoTransaccionID === 6 ? "row-devolucion" : "")}
            sx={{
              width: "100%",
              border: 0,
              // --- estilos de tabla (igual a desktop) ---
              "& .MuiDataGrid-footerContainer": { display: "none !important" },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": { color: "white !important", lineHeight: "45px !important" },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": { color: "white !important" },
              "& .MuiDataGrid-menuIconButton": { color: "rgb(244, 177, 14) !important" },
              "& .MuiInputBase-root": { color: "rgb(244, 177, 14) !important" },
              "& .MuiTablePagination-root": { color: "black !important" },
              "& .MuiSelect-icon": { color: "rgb(244, 177, 14) !important" },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": { backgroundColor: "#F7F7F7" },
                "&:hover": { backgroundColor: "#E3EAFD !important", transition: "background 0.3s ease" },
              },
              "& .row-devolucion": {
                backgroundColor: alpha("#9e9e9e !important", 0.05),
                "&:hover": { backgroundColor: alpha("#9e9e9e !important", 0.1) },
              },
              "& .MuiDataGrid-virtualScroller": { lineHeight: "none !important" },
              "& .MuiDataGrid-footerContainer": {
                position: "sticky",
                bottom: 0,
                backgroundColor: "#ffffff",
                borderTop: "1px solid #ddd",
                zIndex: 10,
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": { width: "2px" },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": { backgroundColor: "#888", borderRadius: "10px" },
            }}
            components={{ Pagination: () => null }}
          />
        </Paper>

        {/* Footer (paginación) */}
        <Box sx={{ flexShrink: 0, p: 1 }}>
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={montoTotalTransacciones}
            vieneDe={"tablaVentas"}
            onPageChange={(newPage) => setPage(newPage)}
          />
        </Box>

        {/* Modales */}
        {openModal === true ? (
          <ModalViewTablaVenta
            openModal={openModal}
            handleCloseModal={handleCloseModal}
            selectedTransaccion={selectedTransaccion}
          />
        ) : null}

        {openModalDevolucion === true ? (
          <ModalViewTablaDevolucion
            openModal={openModalDevolucion}
            handleCloseModal={handleCloseModal}
            selectedTransaccion={selectedTransaccion}
            selectedTransaccionEntidad={selectedTransaccionEntidad}
            transaccionItems={transaccionItems}
          />
        ) : null}

        <GraficasVentasModal 
          open={openGraficasModal} // abrir modal de graficas
          handleClose={handleCloseGraficasModal} // cerrar modal de graficas
          data={datosGraficas} // datos para graficas
          loading={loadingGraficas} // estado de carga para graficas
        />
      </ThemeProvider>
    </div>
  )
}
